import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from './ui/alert-dialog';
import { __setConfirmImpl } from '@/lib/confirm';

/* Mounts ONE AlertDialog and wires the imperative confirm() helper so any
   component anywhere can call `await confirm(...)` without prop drilling. */
export const ConfirmProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolveRef = useRef(null);

  const close = useCallback((result) => {
    setOpen(false);
    const r = resolveRef.current;
    resolveRef.current = null;
    if (r) r(result);
  }, []);

  const trigger = useCallback((options = {}) => new Promise((resolve) => {
    // If a previous prompt is still up (rare race), resolve it false first.
    if (resolveRef.current) resolveRef.current(false);
    resolveRef.current = resolve;
    setOpts(options);
    setOpen(true);
  }), []);

  useEffect(() => {
    __setConfirmImpl(trigger);
    return () => __setConfirmImpl(null);
  }, [trigger]);

  return (
    <>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{opts.title || 'Are you sure?'}</AlertDialogTitle>
            {opts.message && (
              <AlertDialogDescription className="whitespace-pre-line">
                {opts.message}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => close(false)}>
              {opts.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              className={opts.destructive
                ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500'
                : undefined}
            >
              {opts.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConfirmProvider;
